import type { ReactElement } from 'react';
import { Pagination } from 'react-bootstrap';
import { calculateStartPage } from '@devexpress/dx-grid-core';

interface PageButtonsProps {
  currentPage: number;
  totalPages: number;
  onCurrentPageChange: (page: number) => void;
}

const renderPageButtons = (props: PageButtonsProps): ReactElement[] => {
  const { currentPage, totalPages, onCurrentPageChange } = props;

  const pageButtons = [];
  const maxButtonCount = 3;
  let startPage = 1;
  let endPage = totalPages || 1;

  if (maxButtonCount < totalPages) {
    startPage = calculateStartPage(currentPage + 1, maxButtonCount, totalPages);
    endPage = (startPage + maxButtonCount) - 1;
  }

  if (startPage > 1) {
    pageButtons.push((
      <Pagination.Item
        key={ 1 }
        onClick={ () => onCurrentPageChange(0) }
      >
        { 1 }
      </Pagination.Item>
    ));

    if (startPage > 2) {
      pageButtons.push((
        <Pagination.Ellipsis key='ellipsisStart' disabled />
      ));
    }
  }

  for (let page = startPage;page <= endPage;page += 1) {
    pageButtons.push((
      <Pagination.Item
        key={ page }
        onClick={ () => onCurrentPageChange(page - 1) }
        active={ page === currentPage + 1 }
        disabled={ startPage === endPage }
      >
        { page }
      </Pagination.Item>
    ));
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pageButtons.push((
        <Pagination.Ellipsis key='ellipsisEnd' disabled />
      ));
    }

    pageButtons.push((
      <Pagination.Item
        key={ totalPages }
        onClick={ () => onCurrentPageChange(totalPages - 1) }
      >
        { totalPages }
      </Pagination.Item>
    ));
  }

  return pageButtons;
};

export interface DataTablePaginationProps {
  totalPages: number;
  currentPage: number;
  onCurrentPageChange: (page: number) => void;
}

const DataTablePagination = (props: DataTablePaginationProps) => {
  const {
    totalPages,
    currentPage,
    onCurrentPageChange,
  } = props;

  const onPrevPageClick = () => {
    onCurrentPageChange(currentPage > 0 ? currentPage - 1 : 0);
  };
  const onNextPageClick = () => {
    onCurrentPageChange(currentPage < (totalPages - 1) ? currentPage + 1 : totalPages - 1);
  };

  return (
    <Pagination className='float-start'>
      <Pagination.Prev disabled={ currentPage === 0 } onClick={ onPrevPageClick } />

      { renderPageButtons({ currentPage, totalPages, onCurrentPageChange }) }

      <Pagination.Next disabled={ currentPage === totalPages - 1 } onClick={ onNextPageClick } />
    </Pagination>
  );
};

export default DataTablePagination;
